"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";

// Note: This is a client component so metadata must be added to layout.tsx or parent

// ─── Types ────────────────────────────────────────────────────────────────────

interface Workshop {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  category: string;
  /** ISO date string — the day the webinar goes live, e.g. "2026-08-19" */
  eventDate: string;
  date: string;
  time: string;
  tags?: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * A workshop is "upcoming" while today is still on or before its event date.
 * From the day after onward it becomes "previous".
 * Cut-off: eventDate + 1 day (i.e. Aug 19 event → previous from Aug 20 00:00 local).
 */
function isUpcoming(eventDate: string): boolean {
  const cutoff = new Date(eventDate);
  cutoff.setDate(cutoff.getDate() + 1); // becomes previous from the next day
  return new Date() < cutoff;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const workshops: Workshop[] = [
  {
    id: "1",
    slug: "draft-mode-live-from-blank-page-to-boardroom-ready",
    title: "From Blank Page to Boardroom-Ready, Live",
    excerpt:
      "Watch sensei. DRAFT Mode build a procurement policy, an S&OP deck, a supplier agreement and a KPI dashboard live, in one 60-minute session.",
    image: "/blank-page-boardroom-ready.png",
    category: "DRAFT Mode",
    eventDate: "2026-08-26",
    date: "Aug 26, 2026",
    time: "4:00 PM UK Time",
    tags: ["DRAFT Mode", "Free"],
  },
];

const STATUS_OPTIONS = ["All Workshops", "Upcoming Webinars", "Previous Webinars"] as const;
type StatusOption = (typeof STATUS_OPTIONS)[number];

// ─── Workshop Card ────────────────────────────────────────────────────────────

function WorkshopCard({ workshop }: { workshop: Workshop }) {
  const upcoming = isUpcoming(workshop.eventDate);

  return (
    <Link href={`/workshops/${workshop.slug}`} className="blog-article-card d-supplychain">
      {/* Image */}
      <div className="blog-article-image">
        <Image
          src={workshop.image}
          alt={workshop.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          loading="eager"
          priority
          style={{ objectFit: "cover" }}
        />
        <span className="blog-article-badge">{workshop.category}</span>
      </div>

      {/* Content */}
      <div className="bc-content">
        <div className="bc-meta">
          {workshop.date} · {workshop.time}
        </div>
        <h3 className="bc-title">{workshop.title}</h3>
        <p className="bc-excerpt">{workshop.excerpt}</p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
          {/* Status pill — auto-derived from date */}
          {upcoming ? (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                background: "var(--magenta, #EC4899)",
                borderRadius: 100,
                padding: "3px 10px",
                letterSpacing: "0.04em",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#fff",
                  display: "inline-block",
                  animation: "pulseGlow 1.8s ease-in-out infinite",
                }}
              />
              Live
            </span>
          ) : (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--ink-mute)",
                background: "var(--paper-3)",
                border: "1px solid var(--line-strong)",
                borderRadius: 100,
                padding: "3px 10px",
              }}
            >
              Recording Available
            </span>
          )}

          {/* Static tags (excluding "Live" — managed automatically above) */}
          {workshop.tags
            ?.filter((t) => t !== "Live")
            .map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--ink-mute)",
                  background: "var(--paper-3)",
                  border: "1px solid var(--line-strong)",
                  borderRadius: 100,
                  padding: "3px 10px",
                }}
              >
                {tag}
              </span>
            ))}
        </div>
      </div>
    </Link>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function WorkshopsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<StatusOption>("All Workshops");

  const filtered = useMemo(() => {
    return workshops.filter((w) => {
      const matchesSearch =
        searchQuery === "" ||
        w.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.category.toLowerCase().includes(searchQuery.toLowerCase());

      const upcoming = isUpcoming(w.eventDate);
      const matchesStatus =
        selectedStatus === "All Workshops" ||
        (selectedStatus === "Upcoming Webinars" && upcoming) ||
        (selectedStatus === "Previous Webinars" && !upcoming);

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  return (
    <div className="blog-articles-page">

      {/* Hero */}
      <div className="blog-hero-section">
        <div className="blog-hero-icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <h1 className="blog-hero-heading">
          Live <span className="grad-text">Workshops</span>
        </h1>
        <p className="blog-hero-description">
          Hands-on sessions where you watch sensei. work live. Free to attend, packed with real demos and open Q&amp;A.
        </p>
      </div>

      {/* Toolbar */}
      <div className="blog-articles-header">
        <div className="blog-articles-toolbar">
          <div className="search-box">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="search"
              placeholder="Search workshops..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="domain-select">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as StatusOption)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <span className="chev">▼</span>
          </div>
        </div>

        <div className="results-count">
          Showing <strong>{filtered.length}</strong> of{" "}
          <strong>{workshops.length}</strong> workshops
        </div>
      </div>

      {/* Grid */}
      <div className="blog-articles-grid">
        {filtered.length > 0 ? (
          filtered.map((w) => <WorkshopCard key={w.id} workshop={w} />)
        ) : (
          <div className="diag-empty">
            <p>No workshops found matching your criteria.</p>
          </div>
        )}
      </div>

    </div>
  );
}
